/**
 * JWT Service for frontend token management
 */

// Removed unused interface

interface AdminData {
  id: string;
  email: string;
  full_name: string;
  role: string;
  token: string;
  expiresAt: string;
  expiresIn: string;
}

interface UserData {
  id: string;
  unique_id: string;
  mobile_number: string;
  email_id?: string;
  user_name: string;
  token: string;
  expiresAt: string;
  expiresIn: string;
}

class JWTService {
  private readonly ADMIN_STORAGE_KEY = 'adminData';
  private readonly USER_STORAGE_KEY = 'userData';

  /**
   * Store admin token and data
   */
  storeAdminToken(adminData: AdminData): void {
    localStorage.setItem(this.ADMIN_STORAGE_KEY, JSON.stringify(adminData));
  }

  /**
   * Get admin token and data
   */
  getAdminToken(): AdminData | null {
    try {
      const data = localStorage.getItem(this.ADMIN_STORAGE_KEY);
      if (!data) return null;
      
      const parsed = JSON.parse(data);
      
      // Check if token is expired
      if (this.isTokenExpired(parsed.expiresAt)) {
        this.clearAdminToken();
        return null;
      }
      
      return parsed;
    } catch (error) {
      this.clearAdminToken();
      return null;
    }
  }

  /**
   * Clear admin token
   */
  clearAdminToken(): void {
    localStorage.removeItem(this.ADMIN_STORAGE_KEY);
  }

  /**
   * Store user token and data
   */
  storeUserToken(userData: UserData): void {
    localStorage.setItem(this.USER_STORAGE_KEY, JSON.stringify(userData));
  }

  /**
   * Get user token and data
   */
  getUserToken(): UserData | null {
    try {
      const data = localStorage.getItem(this.USER_STORAGE_KEY);
      if (!data) return null;
      
      const parsed = JSON.parse(data);
      
      // Check if token is expired
      if (this.isTokenExpired(parsed.expiresAt)) {
        this.clearUserToken();
        return null;
      }
      
      return parsed;
    } catch (error) {
      this.clearUserToken();
      return null;
    }
  }

  /**
   * Clear user token
   */
  clearUserToken(): void {
    localStorage.removeItem(this.USER_STORAGE_KEY);
  }

  /**
   * Check if token is expired
   */
  private isTokenExpired(expiresAt: string): boolean {
    try {
      const expirationDate = new Date(expiresAt);
      const now = new Date();
      return now >= expirationDate;
    } catch (error) {
      return true; // If we can't parse the date, consider it expired
    }
  }

  /**
   * Get time remaining until token expires
   */
  getTimeRemaining(expiresAt: string): number {
    try {
      const expirationDate = new Date(expiresAt);
      const now = new Date();
      const remaining = expirationDate.getTime() - now.getTime();
      return Math.max(0, remaining);
    } catch (error) {
      return 0;
    }
  }

  /**
   * Format time remaining as human readable string
   */
  formatTimeRemaining(expiresAt: string): string {
    const remaining = this.getTimeRemaining(expiresAt);
    
    if (remaining <= 0) {
      return 'Expired';
    }

    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  /**
   * Check if admin is authenticated
   */
  isAdminAuthenticated(): boolean {
    const adminData = this.getAdminToken();
    return adminData !== null;
  }

  /**
   * Check if user is authenticated
   */
  isUserAuthenticated(): boolean {
    const userData = this.getUserToken();
    return userData !== null;
  }

  /**
   * Get admin authorization header
   */
  getAdminAuthHeader(): string | null {
    const adminData = this.getAdminToken();
    return adminData ? `Bearer ${adminData.token}` : null;
  }

  /**
   * Get user authorization header
   */
  getUserAuthHeader(): string | null {
    const userData = this.getUserToken();
    return userData ? `Bearer ${userData.token}` : null;
  }
}

const jwtService = new JWTService();
export default jwtService;
